import React from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { ValidationScope, ValidatingTextArea } from '@/components/forms/validation';
import { useUniqueId, useValidationManager } from '@/utils/hooks';
import InfoIconWithTooltip from '@/seller/components/InfoIconWithTooltip';
import { ExternalAgreementSignMode } from './ExternalAgreementApproveWizard';
import theme from './ExternalAgreementApproveWizard.module.scss';
import { DescriptiveRadio } from '@/components/forms/Radio';
import { Checkbox } from '@ygtechnologies/bygr-frontend-component-library';

export interface ExternalAgreementApproveOfferOptions {
    offerMessage: string;
    signMode: ExternalAgreementSignMode;
    showSubtotalsToBuyers: boolean;
}
export class ExternalAgreementApproveOfferOptions {
    static empty(): ExternalAgreementApproveOfferOptions {
        return {
            offerMessage: '',
            signMode: ExternalAgreementSignMode.Unset,
            showSubtotalsToBuyers: false,
        };
    }
}

export interface ExternalAgreementApproveWizardOfferProps {
    data: ExternalAgreementApproveOfferOptions;
    onChange(data: ExternalAgreementApproveOfferOptions): void;
    hasSingleParticipant: boolean;
}

const ExternalAgreementApproveWizardOffer: React.FC<ExternalAgreementApproveWizardOfferProps> = ({
    onChange,
    data,
    hasSingleParticipant,
}) => {
    const validationManager = useValidationManager([
        {
            isValid: () => data.signMode !== ExternalAgreementSignMode.Unset,
            // message: () => <Trans>Please </Trans>,
            summaryMessage: () => <Trans>Please choose if the agreement is signed or not</Trans>,
        },
    ]);
    const radioName = useUniqueId();

    return (
        <ValidationScope validationManager={validationManager}>
            <div className="form-label">
                <div className={theme.smallHeader}>
                    <Trans>Message to buyer</Trans>
                    <InfoIconWithTooltip
                        color="light"
                        title={<Trans>Message to buyer</Trans>}
                        content={<Trans>Please note, this message will be visible to the buyer.</Trans>}
                    />
                </div>
                <ValidatingTextArea
                    placeholder={t`You can add a message to the buyer to explain the offer`}
                    className="form-control"
                    autoResize
                    minRows={4}
                    value={data.offerMessage}
                    onChange={(e) => onChange({ ...data, offerMessage: e.target.value })}
                />
                {hasSingleParticipant && (
                    <div className="flex column">
                        <DescriptiveRadio
                            name={radioName}
                            onChange={() => onChange({ ...data, signMode: ExternalAgreementSignMode.PreSigned })}
                            checked={data.signMode === ExternalAgreementSignMode.PreSigned}
                            text={<Trans>This agreement is already signed</Trans>}
                            description={<Trans>This will create a signed change request for this unit</Trans>}
                        />
                        <DescriptiveRadio
                            name={radioName}
                            onChange={() => onChange({ ...data, signMode: ExternalAgreementSignMode.Unsigned })}
                            checked={data.signMode === ExternalAgreementSignMode.Unsigned}
                            text={<Trans>This agreement is not signed</Trans>}
                            description={
                                <Trans>
                                    This is an offer the buyer can evaluate and sign. This will create a change request with
                                    an offer that is already sent to the buyer for signature.
                                </Trans>
                            }
                        />
                    </div>
                )}

                {hasSingleParticipant && (
                    <Checkbox
                        checked={data.showSubtotalsToBuyers}
                        onChange={(e) => onChange({ ...data, showSubtotalsToBuyers: e.target.checked })}>
                        <Trans>Show subtotals to buyers</Trans>
                    </Checkbox>
                )}
            </div>
        </ValidationScope>
    );
};

export default ExternalAgreementApproveWizardOffer;